import { useMemo } from 'react'
import { useConfig } from 'hooks'
import { modifiers } from 'helpers'

import { useVaultsQuery } from 'graphql/subgraph/vaults'
import type { VaultsQueryPayload } from 'graphql/subgraph/vaults'

import getRequestVariables from './getRequestVariables'


type Input = Pick<VaultsPage.SortFormParams, 'search' | 'sort'> & {
  skip: number
  limit: number
  blacklist: string[]
}

type Output = {
  data: VaultList.Vault[]
  isFetching: boolean
}

type ModifyVaultsInput = {
  data: VaultsQueryPayload | null
  blacklist: string[]
}

type ModifyVaultsType = VaultList.SubgraphVault & {
  isBlacklisted: boolean
}

const _modifyVaults = (values: ModifyVaultsInput): VaultList.Vault[] => {
  const { data, blacklist } = values

  if (!data?.vaults) {
    return []
  }

  return data.vaults
    .filter((vault) => !blacklist.includes(vault.address))
    .map((vault) => modifiers.modifyVaultData<ModifyVaultsType>({
      ...vault,
      isBlacklisted: false,
    }))
}

const useAllVaults = (input: Input): Output => {
  const { sort, search, limit, skip, blacklist } = input

  const { sdk } = useConfig()

  const variables = getRequestVariables({ filter: 'all', sort, limit, skip, search } )

  const { data, isFetching } = useVaultsQuery({
    resetDataKeys: [ 'orderBy' ],
    urls: sdk.config.api,
    variables,
  })

  return useMemo(() => ({
    data: _modifyVaults({ data, blacklist }),
    isFetching,
  }), [ data, blacklist, isFetching ])
}


export default useAllVaults
